import type { ReactNode } from 'react';
import { Check, Loader2, AlertCircle, Play, RotateCcw } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import type { PhaseStatus } from './phase-status';

const STATUS_BADGE: Record<PhaseStatus, { label: string; cls: string }> = {
  idle: { label: 'À lancer', cls: 'bg-ink-100 text-ink-500' },
  running: { label: 'En cours…', cls: 'bg-primary-50 text-primary-600' },
  done: { label: 'Terminé', cls: 'bg-emerald-50 text-emerald-600' },
  error: { label: 'Erreur', cls: 'bg-red-50 text-red-600' },
};

export interface PhaseCardProps {
  index: number;
  title: string;
  description: string;
  status: PhaseStatus;
  /** La phase est lançable (prérequis remplis, rien d'autre en cours). */
  canRun: boolean;
  onRun: () => void;
  error?: string | null;
  /** Aperçu des blocs produits (rendu sous la description). */
  children?: ReactNode;
  /** Masque le bouton « Lancer » intégré (ex. mode dictée piloté par les enfants). */
  hideRunButton?: boolean;
}

export function PhaseCard({
  index,
  title,
  description,
  status,
  canRun,
  onRun,
  error,
  children,
  hideRunButton = false,
}: PhaseCardProps) {
  const badge = STATUS_BADGE[status];
  const running = status === 'running';
  const isDone = status === 'done';

  return (
    <div
      className={cn(
        'border-ink-200 rounded-2xl border bg-white p-4 sm:p-5',
        running && 'border-primary-300',
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-bold',
              isDone ? 'bg-emerald-100 text-emerald-600' : 'bg-ink-100 text-ink-500',
            )}
          >
            {isDone ? <Check className="h-4 w-4" /> : index}
          </span>
          <h2 className="font-display text-ink-900 text-[15px] font-extrabold">{title}</h2>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2.5 py-1 font-sans text-[11px] font-semibold',
            badge.cls,
          )}
        >
          {badge.label}
        </span>
      </div>

      <p className="text-ink-500 mb-3 font-sans text-[13px]">{description}</p>

      {children}

      {error && (
        <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 font-sans text-[12px] text-red-600">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      )}

      {!hideRunButton && (
        <Button
          type="button"
          onClick={onRun}
          disabled={!canRun}
          className="mt-3 min-h-11 w-full active:scale-[0.98]"
        >
          {running ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isDone ? (
            <RotateCcw className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          {running ? 'Génération…' : isDone ? 'Relancer' : 'Lancer'}
        </Button>
      )}
    </div>
  );
}
